import { LakehouseConnectionTypeEnum, Uni } from '@vendia/management-api-types'
import dayjs from 'dayjs'
import { useState } from 'react'
import Button from 'src/components/buttons/button'
import Card, { CardRule } from 'src/components/containers/card'
import Alert from 'src/components/messages/alert'
import ConfirmationModal from 'src/components/modals/confirmation-modal'
import { Table } from 'src/types/types'
import { TaskRun, TaskRunExecutionStatus, useListTaskRuns } from 'src/utils/hooks/use-list-task-runs'
import { useRequestTaskRun } from 'src/utils/hooks/use-request-task-run'
import { getFriendlyJobStatus } from 'src/utils/lakehouse/products'
import { getSourceTableInfo } from 'src/utils/lakehouse/tables'
import notify from 'src/utils/notify'

export const SyncStatusCard = ({ table, nodeName }: { uni: Uni; nodeName: string; table: Table }) => {
  const [expanded, setExpanded] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)

  const isOwnedTable = table._owner === nodeName

  const taskRunQuery = useListTaskRuns({ typeFilter: 'TABLE', tableId: table._id })
  const { refetch: refetchTaskRuns } = taskRunQuery
  const requestTaskRunMutation = useRequestTaskRun()

  const taskRuns = taskRunQuery.data?.taskRuns ?? []

  const getUserFriendlyTaskStatus = (status: TaskRunExecutionStatus): string => {
    return getFriendlyJobStatus[status]
  }
  const sourceInfo = getSourceTableInfo(table)

  const TaskRunItem = ({ taskRun }: { taskRun: TaskRun }) => {
    const isFailed = taskRun.status === 'FAILED' || getUserFriendlyTaskStatus(taskRun?.status) === 'Failure'
    return (
      <>
        <div>
          <div className='flex items-center justify-between'>
            <div>
              {taskRun.status === 'RUNNING' ? (
                <>
                  <div className='text-base font-bold'>Started {dayjs(taskRun.startTime).fromNow()}</div>
                  <div className='text-xs font-normal'>{dayjs(taskRun.startTime).format('MMMM D, YYYY h:mm A')}</div>
                </>
              ) : (
                <>
                  <div className='text-base font-bold'>{dayjs(taskRun.endTime).fromNow()}</div>
                  <div className='text-xs font-normal'>{dayjs(taskRun.endTime).format('MMMM D, YYYY h:mm A')}</div>
                </>
              )}
            </div>
            <div className='flex items-center gap-2'>
              <div
                className={`text-base font-bold ${
                  taskRun?.status === 'RUNNING'
                    ? 'text-information-10'
                    : taskRun?.status === 'SUCCEEDED'
                      ? 'text-success-13'
                      : getUserFriendlyTaskStatus(taskRun?.status) === 'Failure'
                        ? 'text-error-8'
                        : ''
                }`}
              >
                {getUserFriendlyTaskStatus(taskRun?.status)}
              </div>
            </div>
          </div>
          {(sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Upload ||
            sourceInfo?.connectionType === LakehouseConnectionTypeEnum.S3) &&
            taskRun.artifacts &&
            taskRun.artifacts.length > 0 && (
              <div className='mt-1 flex items-center gap-2'>
                <div className='text-xs text-red-600'>Click for error info</div>
                <Button
                  kind='icon'
                  size='small'
                  icon='download'
                  label='Download run details'
                  href={taskRun.artifacts[0].temporaryUrl}
                  target='_blank'
                />
              </div>
            )}
        </div>
        {isFailed && taskRun.errorMessage && (
          <div className='mt-1 text-xs text-error-8'>
            {taskRun.errorMessage}
          </div>
        )}
        {(sourceInfo?.connectionType === LakehouseConnectionTypeEnum.S3 ||
          sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Upload) &&
          taskRun.errorMessage &&
          taskRun.errorMessage.includes('Line') &&
          !isFailed && (
            <div className='text-error-8 mt-1 text-xs'>{taskRun.errorMessage}</div>
          )}
      </>
    )
  }

  return (
    <>
      <Card className='w-full' padding='none' overflow='visible'>
        <div>
          <div className='flex items-center justify-between p-6 text-sm font-bold'>
            <h4>Syncing</h4>
            <div className='flex gap-2'>
              {isOwnedTable && (
                <Button
                  kind='icon'
                  size='small'
                  icon={'refresh'}
                  label='Sync table'
                  onClick={() => setShowSyncModal(true)}
                  disabled={taskRuns?.some((taskRun: TaskRun) => taskRun?.status === 'RUNNING')}
                />
              )}
              <Button
                kind='icon'
                size='small'
                icon={expanded ? 'chevron-up' : 'chevron-down'}
                label='Toggle status details'
                onClick={() => setExpanded(!expanded)}
              />
            </div>
          </div>
          {expanded && (
            <div>
              <CardRule />
              <div className='bg-uibg-0 p-6'>
                <div className='space-y-4'>
                  {taskRunQuery.isLoading ? (
                    <div className='text-gray-500'>Loading...</div>
                  ) : !taskRuns || taskRuns.length === 0 ? (
                    <div className='text-gray-500'>Not yet synced</div>
                  ) : (
                    <ul className='space-y-4'>
                      {taskRuns.map((taskRun: TaskRun, index: number) => (
                        <li key={index}>
                          <TaskRunItem taskRun={taskRun} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
      <ConfirmationModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        title={`Confirm sync for ${table?.name}`}
        onSubmit={() =>
          requestTaskRunMutation.mutate(
            { tableId: (table as Table)._id! },
            {
              onSuccess: (result) => {
                if (result?.isRequested) {
                  notify.success(`Successfully initiated sync for: ${table.name}`)
                  setShowSyncModal(false)
                  setTimeout(() => {
                    refetchTaskRuns()
                  }, 6000) // task run creation is async
                } else {
                  notify.error('Failed to request task run')
                }
              },
              onError: (error) => {
                notify.error(`Error syncing table: ${error.message}`)
              },
            },
          )
        }
        isSubmitting={requestTaskRunMutation.isPending}
        actionButtonType={`primary`}
        actionButtonText={`Sync`}
        confirmationText={'sync'}
      >
        <div className='grid gap-4'>
          <p>
            Immediately begin syncing the latest data from your configured source to <strong>{table.name}</strong>
          </p>
          <Alert>Note: Once started, the sync process cannot be cancelled or undone.</Alert>
        </div>
      </ConfirmationModal>
    </>
  )
}
